/**
 * Workshop Supabase Service Layer
 * Handles all DB operations — replaces localStorage
 */
import { createClient } from '@/lib/supabase/client';

export interface ServicePrice {
  id: number;
  name: string;
  offlinePrice: number;
  onlinePrice: number;
  rating: number;
  duration: string;
}

export interface BusinessSettings {
  referralRewardAmount: number;
  walletCashbackPercent: number;
  servicePrices: ServicePrice[];
}

// ─── Dynamic Service (from `services` table) ─────────────────────────────────

export interface DynamicService {
  id: string;
  name: string;
  price: number;
  offlinePrice: number;
  durationMinutes: number;
  description: string;
  rating: number;
  isActive: boolean;
}

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  vehicleNumber: string;
  role: 'admin' | 'staff' | 'customer';
  pin: string;
  walletBalance: number;
}

export interface BookingRecord {
  id: string;
  customerId?: string;
  customerName: string;
  phone: string;
  serviceName: string;
  bikeNumber: string;
  slot: string;
  amount: number;
  status: 'pending' | 'confirmed' | 'inprogress' | 'completed' | 'cancelled';
  paymentMode: string;
  paid: boolean;
  cashbackCredited: boolean;
  createdAt?: string;
}

export interface ReferralRecord {
  id?: string;
  referrerId?: string;
  newUserId?: string;
  referrerPhone: string;
  newUserPhone: string;
  status: 'pending' | 'completed';
  rewardAmount: number;
  createdAt?: string;
}

export const DEFAULT_SERVICE_PRICES: ServicePrice[] = [
  { id: 1, name: 'फुल बाइक सर्विस', offlinePrice: 350, onlinePrice: 299, rating: 4.9, duration: '90 मिनट' },
  { id: 2, name: 'इंजन ऑयल चेंज', offlinePrice: 150, onlinePrice: 120, rating: 4.8, duration: '30 मिनट' },
  { id: 3, name: 'वॉशिंग & पॉलिश', offlinePrice: 100, onlinePrice: 80, rating: 5.0, duration: '45 मिनट' },
  { id: 4, name: 'चेन ल्यूब & सेटिंग', offlinePrice: 80, onlinePrice: 60, rating: 4.7, duration: '20 मिनट' },
  { id: 5, name: 'टायर पंचर रिपेयर', offlinePrice: 60, onlinePrice: 50, rating: 4.6, duration: '20 मिनट' },
  { id: 6, name: 'ब्रेक पैड बदलना', offlinePrice: 200, onlinePrice: 165, rating: 4.8, duration: '40 मिनट' },
];

const DEFAULT_SETTINGS: BusinessSettings = {
  referralRewardAmount: 50,
  walletCashbackPercent: 2,
  servicePrices: DEFAULT_SERVICE_PRICES,
};

// ─── Config Table (key-value settings) ───────────────────────────────────────

export async function getConfigValue(key: string): Promise<string | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('config')
      .select('value')
      .eq('key', key)
      .single();
    if (error || !data) return null;
    return data.value;
  } catch {
    return null;
  }
}

export async function setConfigValue(key: string, value: string): Promise<boolean> {
  try {
    const supabase = createClient();
    const { error } = await supabase
      .from('config')
      .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
    if (error) {
      console.error('setConfigValue error:', (error as any).message);
      return false;
    }
    return true;
  } catch (err) {
    console.error('setConfigValue exception:', err);
    return false;
  }
}

export async function getGlobalSettings(): Promise<{ referralRewardAmount: number; walletCashbackPercent: number }> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('config')
      .select('key, value')
      .in('key', ['referral_reward_amount', 'wallet_cashback_percent']);

    if (error || !data || data.length === 0) {
      return { referralRewardAmount: 50, walletCashbackPercent: 2 };
    }

    const map: Record<string, string> = {};
    data.forEach((row: { key: string; value: string }) => { map[row.key] = row.value; });

    return {
      referralRewardAmount: Number(map['referral_reward_amount'] ?? 50),
      walletCashbackPercent: Number(map['wallet_cashback_percent'] ?? 2),
    };
  } catch {
    return { referralRewardAmount: 50, walletCashbackPercent: 2 };
  }
}

export async function saveGlobalSettings(referralRewardAmount: number, walletCashbackPercent: number): Promise<boolean> {
  try {
    const supabase = createClient();
    const { error } = await supabase
      .from('config')
      .upsert([
        { key: 'referral_reward_amount', value: String(referralRewardAmount), updated_at: new Date().toISOString() },
        { key: 'wallet_cashback_percent', value: String(walletCashbackPercent), updated_at: new Date().toISOString() },
      ], { onConflict: 'key' });

    if (error) {
      console.error('saveGlobalSettings error:', (error as any).message);
      return false;
    }
    return true;
  } catch (err) {
    console.error('saveGlobalSettings exception:', err);
    return false;
  }
}

// ─── Dynamic Services CRUD ────────────────────────────────────────────────────

function rowToService(row: Record<string, unknown>): DynamicService {
  return {
    id: row.id as string,
    name: row.name as string,
    price: Number(row.price),
    offlinePrice: Number(row.offline_price),
    durationMinutes: Number(row.duration_minutes),
    description: (row.description as string) || '',
    rating: Number(row.rating),
    isActive: Boolean(row.is_active),
  };
}

export async function getAllServices(): Promise<DynamicService[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('getAllServices error:', error.message);
      return [];
    }
    if (!data) return [];
    return data.map(rowToService);
  } catch (err) {
    console.error('getAllServices exception:', err);
    return [];
  }
}

export async function getAllServicesAdmin(): Promise<DynamicService[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) {
      console.error('getAllServicesAdmin error:', error.message);
      return [];
    }
    if (!data) return [];
    return data.map(rowToService);
  } catch (err) {
    console.error('getAllServicesAdmin exception:', err);
    return [];
  }
}

export async function createService(service: Omit<DynamicService, 'id' | 'isActive'>): Promise<DynamicService | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('services')
      .insert({
        name: service.name,
        price: service.price,
        offline_price: service.offlinePrice,
        duration_minutes: service.durationMinutes,
        description: service.description,
        rating: service.rating,
        is_active: true,
      })
      .select()
      .single();

    if (error || !data) {
      console.error('createService error:', (error as any)?.message);
      return null;
    }
    return rowToService(data);
  } catch (err) {
    console.error('createService exception:', err);
    return null;
  }
}

export async function updateService(id: string, updates: Partial<Omit<DynamicService, 'id'>>): Promise<boolean> {
  try {
    const supabase = createClient();
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.price !== undefined) payload.price = updates.price;
    if (updates.offlinePrice !== undefined) payload.offline_price = updates.offlinePrice;
    if (updates.durationMinutes !== undefined) payload.duration_minutes = updates.durationMinutes;
    if (updates.description !== undefined) payload.description = updates.description;
    if (updates.rating !== undefined) payload.rating = updates.rating;
    if (updates.isActive !== undefined) payload.is_active = updates.isActive;

    const { error } = await supabase
      .from('services')
      .update(payload)
      .eq('id', id);

    if (error) {
      console.error('updateService error:', (error as any).message);
      return false;
    }
    return true;
  } catch (err) {
    console.error('updateService exception:', err);
    return false;
  }
}

export async function deleteService(id: string): Promise<boolean> {
  try {
    const supabase = createClient();
    const { error } = await supabase
      .from('services')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('deleteService error:', (error as any).message);
      return false;
    }
    return true;
  } catch (err) {
    console.error('deleteService exception:', err);
    return false;
  }
}

// ─── Business Settings (legacy — kept for backward compat) ───────────────────

export async function getBusinessSettings(): Promise<BusinessSettings> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('business_settings')
      .select('*')
      .eq('id', 1)
      .single();

    // Also try config table for global settings
    const globalSettings = await getGlobalSettings();

    if (error || !data) {
      await supabase.from('business_settings').upsert({
        id: 1,
        referral_reward_amount: globalSettings.referralRewardAmount,
        wallet_cashback_percent: globalSettings.walletCashbackPercent,
        service_prices: DEFAULT_SETTINGS.servicePrices,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id' });
      return {
        referralRewardAmount: globalSettings.referralRewardAmount,
        walletCashbackPercent: globalSettings.walletCashbackPercent,
        servicePrices: DEFAULT_SERVICE_PRICES,
      };
    }

    const savedPrices: ServicePrice[] = Array.isArray(data.service_prices) ? data.service_prices : [];
    return {
      referralRewardAmount: globalSettings.referralRewardAmount,
      walletCashbackPercent: globalSettings.walletCashbackPercent,
      servicePrices: DEFAULT_SERVICE_PRICES.map((def) => {
        const saved = savedPrices.find((s) => s.id === def.id);
        return saved ? { ...def, ...saved } : def;
      }),
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export async function saveBusinessSettings(settings: BusinessSettings): Promise<boolean> {
  try {
    const supabase = createClient();
    const { error } = await supabase.from('business_settings').upsert({
      id: 1,
      referral_reward_amount: settings.referralRewardAmount,
      wallet_cashback_percent: settings.walletCashbackPercent,
      service_prices: settings.servicePrices,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'id' });

    // Also save to config table
    await saveGlobalSettings(settings.referralRewardAmount, settings.walletCashbackPercent);

    if (error) {
      console.error('saveBusinessSettings error:', (error as any).message);
      return false;
    }
    return true;
  } catch (err) {
    console.error('saveBusinessSettings exception:', err);
    return false;
  }
}

// ─── Bookings ─────────────────────────────────────────────────────────────────

export async function getAllBookings(): Promise<BookingRecord[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('getAllBookings error:', error.message);
      return [];
    }
    if (!data) return [];
    return data.map(rowToBooking);
  } catch (err) {
    console.error('getAllBookings exception:', err);
    return [];
  }
}

export async function getUserBookings(customerId: string): Promise<BookingRecord[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('getUserBookings error:', error.message);
      return [];
    }
    if (!data) return [];
    return data.map(rowToBooking);
  } catch (err) {
    console.error('getUserBookings exception:', err);
    return [];
  }
}

export async function createBooking(booking: BookingRecord): Promise<BookingRecord | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('bookings')
      .insert({
        id: booking.id,
        customer_id: booking.customerId || null,
        customer_name: booking.customerName,
        phone: booking.phone,
        service_name: booking.serviceName,
        bike_number: booking.bikeNumber,
        slot: booking.slot,
        amount: booking.amount,
        status: booking.status,
        payment_mode: booking.paymentMode,
        paid: booking.paid,
        cashback_credited: booking.cashbackCredited,
      })
      .select()
      .single();

    if (error) {
      console.error('createBooking error:', error.message);
      return null;
    }
    if (!data) return null;
    return rowToBooking(data);
  } catch (err) {
    console.error('createBooking exception:', err);
    return null;
  }
}

export async function updateBookingStatus(
  bookingId: string,
  status: BookingRecord['status']
): Promise<void> {
  try {
    const supabase = createClient();
    await supabase
      .from('bookings')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', bookingId);
  } catch {
    // silent
  }
}

export async function markBookingPaid(
  bookingId: string,
  paymentMode: string,
  cashbackCredited: boolean
): Promise<void> {
  try {
    const supabase = createClient();
    await supabase
      .from('bookings')
      .update({
        paid: true,
        payment_mode: paymentMode,
        cashback_credited: cashbackCredited,
        status: 'completed',
        updated_at: new Date().toISOString(),
      })
      .eq('id', bookingId);
  } catch {
    // silent
  }
}

function rowToBooking(row: Record<string, unknown>): BookingRecord {
  return {
    id: row.id as string,
    customerId: row.customer_id as string | undefined,
    customerName: row.customer_name as string,
    phone: row.phone as string,
    serviceName: row.service_name as string,
    bikeNumber: row.bike_number as string,
    slot: row.slot as string,
    amount: Number(row.amount),
    status: row.status as BookingRecord['status'],
    paymentMode: row.payment_mode as string,
    paid: Boolean(row.paid),
    cashbackCredited: Boolean(row.cashback_credited),
    createdAt: row.created_at as string | undefined,
  };
}

// ─── User Profiles / Wallet ───────────────────────────────────────────────────

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('getUserProfile error:', error.message);
      return null;
    }
    if (!data) return null;
    return rowToProfile(data);
  } catch (err) {
    console.error('getUserProfile exception:', err);
    return null;
  }
}

export async function updateWalletBalance(userId: string, newBalance: number): Promise<void> {
  try {
    const supabase = createClient();
    await supabase
      .from('user_profiles')
      .update({ wallet_balance: newBalance, updated_at: new Date().toISOString() })
      .eq('id', userId);
  } catch {
    // silent
  }
}

// ─── Wallet Transactions ──────────────────────────────────────────────────────

export interface WalletTransaction {
  id?: string;
  userId: string;
  type: 'CREDIT' | 'DEBIT';
  amount: number;
  balanceAfter: number;
  description: string;
  referenceId?: string;
  createdAt?: string;
}

export async function addWalletTransaction(tx: Omit<WalletTransaction, 'id' | 'createdAt'>): Promise<void> {
  try {
    const supabase = createClient();
    await supabase.from('wallet_transactions').insert({
      user_id: tx.userId,
      type: tx.type,
      amount: tx.amount,
      balance_after: tx.balanceAfter,
      description: tx.description,
      reference_id: tx.referenceId || null,
    });
  } catch {
    // silent
  }
}

export async function getWalletTransactions(userId: string): Promise<WalletTransaction[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('wallet_transactions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error || !data) return [];
    return data.map((r) => ({
      id: r.id,
      userId: r.user_id,
      type: r.type as 'CREDIT' | 'DEBIT',
      amount: Number(r.amount),
      balanceAfter: Number(r.balance_after),
      description: r.description,
      referenceId: r.reference_id,
      createdAt: r.created_at,
    }));
  } catch {
    return [];
  }
}

// ─── Sequential Job ID ────────────────────────────────────────────────────────

export async function getNextJobId(): Promise<string> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase.rpc('get_next_job_seq');
    if (error || data === null || data === undefined) {
      // Fallback: use timestamp-based ID
      return `JOB-${String(Date.now()).slice(-6)}`;
    }
    return `JOB-${String(data).padStart(4, '0')}`;
  } catch {
    return `JOB-${String(Date.now()).slice(-6)}`;
  }
}

// ─── Pay via Wallet for Booking ───────────────────────────────────────────────

export async function payBookingViaWallet(
  userId: string,
  currentBalance: number,
  amount: number,
  bookingId: string,
  serviceName: string
): Promise<{ success: boolean; newBalance: number; error?: string }> {
  if (currentBalance < amount) {
    return { success: false, newBalance: currentBalance, error: 'insufficient_balance' };
  }
  const newBalance = currentBalance - amount;
  try {
    const supabase = createClient();
    // Deduct from wallet
    const { error: walletErr } = await supabase
      .from('user_profiles')
      .update({ wallet_balance: newBalance, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (walletErr) throw walletErr;

    // Record DEBIT transaction
    await addWalletTransaction({
      userId,
      type: 'DEBIT',
      amount,
      balanceAfter: newBalance,
      description: `सर्विस बुकिंग: ${serviceName}`,
      referenceId: bookingId,
    });

    // Mark booking as paid via wallet
    await supabase
      .from('bookings')
      .update({
        paid: true,
        payment_mode: 'wallet',
        status: 'confirmed',
        updated_at: new Date().toISOString(),
      })
      .eq('id', bookingId);

    return { success: true, newBalance };
  } catch (err) {
    return { success: false, newBalance: currentBalance, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getUserProfileByPhone(phone: string): Promise<UserProfile | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('phone', phone)
      .single();

    if (error || !data) return null;
    return rowToProfile(data);
  } catch {
    return null;
  }
}

function rowToProfile(row: Record<string, unknown>): UserProfile {
  return {
    id: row.id as string,
    email: row.email as string,
    fullName: row.full_name as string,
    phone: row.phone as string,
    vehicleNumber: row.vehicle_number as string,
    role: row.role as UserProfile['role'],
    pin: row.pin as string,
    walletBalance: Number(row.wallet_balance),
  };
}

// ─── Referrals ────────────────────────────────────────────────────────────────

export async function getAllReferrals(): Promise<ReferralRecord[]> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .order('created_at', { ascending: false });

    if (error || !data) return [];
    return data.map((r) => ({
      id: r.id,
      referrerId: r.referrer_id,
      newUserId: r.new_user_id,
      referrerPhone: r.referrer_phone,
      newUserPhone: r.new_user_phone,
      status: r.status as 'pending' | 'completed',
      rewardAmount: Number(r.reward_amount),
      createdAt: r.created_at,
    }));
  } catch {
    return [];
  }
}

export async function createPendingReferral(
  referrerId: string,
  referrerPhone: string,
  newUserId: string,
  newUserPhone: string,
  rewardAmount: number
): Promise<void> {
  try {
    const supabase = createClient();
    await supabase.from('referrals').insert({
      referrer_id: referrerId,
      referrer_phone: referrerPhone,
      new_user_id: newUserId,
      new_user_phone: newUserPhone,
      status: 'pending',
      reward_amount: rewardAmount,
    });
  } catch {
    // silent
  }
}

export async function checkAndCreditReferralReward(
  completedBookingUserId: string
): Promise<string | null> {
  try {
    const supabase = createClient();
    // Find pending referral for this new user
    const { data: referrals, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('new_user_id', completedBookingUserId)
      .eq('status', 'pending')
      .limit(1);

    if (error || !referrals || referrals.length === 0) return null;
    const referral = referrals[0];

    // Credit reward to referrer wallet
    const { data: referrerProfile } = await supabase
      .from('user_profiles')
      .select('wallet_balance')
      .eq('id', referral.referrer_id)
      .single();

    if (referrerProfile) {
      const newBalance = Number(referrerProfile.wallet_balance) + Number(referral.reward_amount);
      await supabase
        .from('user_profiles')
        .update({ wallet_balance: newBalance })
        .eq('id', referral.referrer_id);
    }

    // Mark referral completed
    await supabase
      .from('referrals')
      .update({ status: 'completed' })
      .eq('id', referral.id);

    // Add notification for referrer
    await supabase.from('notifications').insert({
      user_id: referral.referrer_id,
      message: `🎉 बधाई हो! आपके दोस्त ने पहली सर्विस पूरी कर ली है, ₹${referral.reward_amount} आपके वॉलेट में क्रेडिट हो गए।`,
      is_read: false,
    });

    // Also update localStorage wallet as fallback
    if (referral.referrer_phone) {
      try {
        const walletKey = `cw_wallet_${referral.referrer_phone}`;
        const current = Number(localStorage.getItem(walletKey) || '0');
        localStorage.setItem(walletKey, String(current + Number(referral.reward_amount)));
      } catch { /* silent */ }
    }

    return referral.referrer_id;
  } catch {
    return null;
  }
}

// ─── Notifications ────────────────────────────────────────────────────────────

export async function addNotification(userId: string, message: string): Promise<void> {
  try {
    const supabase = createClient();
    await supabase.from('notifications').insert({
      user_id: userId,
      message,
      is_read: false,
    });
  } catch {
    // silent
  }
}

export async function getNotifications(userId: string): Promise<Array<{ message: string; createdAt: string; read: boolean }>> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error || !data) return [];
    return data.map((n) => ({
      message: n.message,
      createdAt: n.created_at,
      read: n.is_read,
    }));
  } catch {
    return [];
  }
}

export async function markNotificationsRead(userId: string): Promise<void> {
  try {
    const supabase = createClient();
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);
  } catch {
    // silent
  }
}

// ─── Daily Auth Sessions ──────────────────────────────────────────────────────

export async function isDailyAuthValid(userId: string): Promise<boolean> {
  try {
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('daily_auth_sessions')
      .select('expires_at')
      .eq('user_id', userId)
      .eq('session_date', today)
      .single();

    if (error || !data) return false;
    return new Date(data.expires_at) > new Date();
  } catch {
    return false;
  }
}

export async function saveDailyAuthSession(userId: string): Promise<void> {
  try {
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);

    await supabase.from('daily_auth_sessions').upsert({
      user_id: userId,
      session_date: today,
      expires_at: endOfDay.toISOString(),
    }, { onConflict: 'user_id,session_date' });
  } catch {
    // silent
  }
}

// ─── Referral Code Helpers ────────────────────────────────────────────────────

export function getReferralCode(phone: string): string {
  return `REF${phone.slice(-6)}`;
}

export function getReferralLink(phone: string): string {
  const baseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : 'https://chaurasiya5073.builtwithrocket.new';
  return `${baseUrl}/?ref=${getReferralCode(phone)}`;
}

export function extractRefCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('ref');
}

// ─── CSV Export ───────────────────────────────────────────────────────────────

export function exportBookingsToCSV(bookings: BookingRecord[]): void {
  const headers = ['Job ID', 'Customer', 'Phone', 'Service', 'Vehicle', 'Slot', 'Amount (₹)', 'Status', 'Payment Mode', 'Paid', 'Cashback Credited', 'Date'];
  const rows = bookings.map((b) => [
    b.id,
    b.customerName,
    b.phone,
    b.serviceName,
    b.bikeNumber,
    b.slot,
    b.amount,
    b.status,
    b.paymentMode,
    b.paid ? 'Yes' : 'No',
    b.cashbackCredited ? 'Yes' : 'No',
    b.createdAt ? new Date(b.createdAt).toLocaleDateString('en-IN') : '',
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `chaurasiya_workshop_bookings_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ─── Send OTP Email via Edge Function ────────────────────────────────────────

export async function sendOtpEmail(email: string, otp: string, name?: string): Promise<{ demo?: boolean; otp?: string }> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase.functions.invoke('send-otp-email', {
      body: { email, otp, name },
    });

    if (error) {
      // Fallback: return OTP for demo display
      return { demo: true, otp };
    }
    return data || { demo: true, otp };
  } catch {
    return { demo: true, otp };
  }
}
